import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCartStore } from '@/features/Cart/stores/cart';
import { useProductBaseStore } from '@/features/Product/stores/productBase';
import { useProductBundleStore } from '@/features/Product/stores/productBundle';
import { useProductCompleteStore } from '@/features/Product/stores/productComplete';
import type { CartItemQuantity, CartRule } from '@/features/Cart/types/models';
import type { ProductComplete } from '@/features/Product/types/models';

export const BULK_PRODUCT_TAG = 'bulk-product';

export const isBulkProduct = (product?: ProductComplete | null): boolean =>
  product?.attributes.tags?.includes(BULK_PRODUCT_TAG) ?? false;

const getBulkProductIds = (): Set<number> => {
  const productBaseStore = useProductBaseStore();
  const productBundleStore = useProductBundleStore();
  const productCompleteStore = useProductCompleteStore();

  return new Set(
    [
      ...productBaseStore.allCachedProducts,
      ...productBundleStore.cachedBundleProducts,
      ...productCompleteStore.cachedDisplayProducts,
    ]
      .filter((product) => product.attributes.tags?.includes(BULK_PRODUCT_TAG))
      .map((product) => product.attributes.globalId),
  );
};

export const cartRuleBulkProductMaxQuantity: CartRule = (cart) => {
  const bulkProductIds = getBulkProductIds();
  if (!bulkProductIds.size) return null;

  const [keptLine, ...olderLines] = cart.items.filter((item) =>
    bulkProductIds.has(item.product_id),
  );
  if (!keptLine) return null;

  const update: CartItemQuantity[] =
    keptLine.quantity > 1 ? [{ key: keptLine.key, quantity: 1 }] : [];
  const remove: CartItemQuantity[] = olderLines.map(({ key }) => ({ key, quantity: 0 }));

  return update.length || remove.length ? { add: [], update, remove } : null;
};

export const useBulkProductInCart = () => {
  const { cartData } = storeToRefs(useCartStore());
  const hasBulkProductInCart = computed(() => {
    const bulkProductIds = getBulkProductIds();
    return cartData.value.items.some((item) => bulkProductIds.has(item.product_id));
  });

  return { hasBulkProductInCart };
};
